import { ArrowRightAltIcon, Color, ClearBadge } from "@bonliva-crm/ui";
import { useState } from "react";
import locale from "@bonliva-crm/locale";
import { Box, Chip, IconButton, Typography, makeStyles } from "@material-ui/core";
import { useFormField } from "@clientUtils";
import { QualityCheckFilterModal } from "./components/quality-check-filter-modal";
import { QualityCheckFilterData } from "./utils";
import { FilterWithFavouriteProps } from "../../utils/filters-record-types";
import { NonCollapsibleFavFilter } from "../_base/non-collapsible-fav-filter";
import { SearchQualityCheckStatus } from "@graphql";

export type QualityCheckFilterProps = FilterWithFavouriteProps;

export function QualityCheckFilter(props: QualityCheckFilterProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const title = props.label ?? locale.QualityCheckDocument;
  const classes = useStyles();

  const {
    setFieldValue,
    field: { value }
  } = useFormField<QualityCheckFilterData>(props.name);

  const handleRemoveStatus = (status: string) => {
    const statuses = value.qualityCheckStatuses.filter((s) => s !== status);

    if (statuses.length === 0) {
      setFieldValue(null, { shouldDirty: true });
      return;
    }

    setFieldValue(
      {
        ...value,
        qualityCheckStatuses: value.qualityCheckStatuses.filter((s) => s !== status)
      },
      { shouldDirty: true }
    );
  };

  const getSearchQualityCheckStatus = (v: string) => {
    if (v === "MRSA_updateOverdue") {
      return "MRSA-uppdatering förfallen";
    } else if (value.qualityCheckName === "Referenser") {
      return locale.searchQualityCheckReferenceStatus[v];
    } else if (value.qualityCheckName === "Referenser Nationella") {
      return locale.searchQualityCheckReferenceNationalStatus[v];
    }

    return locale.searchQualityCheckStatus[v as SearchQualityCheckStatus];
  };

  const modal = (
    <QualityCheckFilterModal
      open={modalOpen}
      initialValue={value}
      onChange={(v) => {
        setFieldValue(v, { shouldDirty: true });
        setModalOpen(false);
      }}
      onClose={() => {
        setModalOpen(false);
      }}
    />
  );

  if (props.setFavorite) {
    return (
      <>
        <NonCollapsibleFavFilter
          favorite={props.favorite}
          onSetFavorite={props.setFavorite}
          title={title}
          onTitleClick={() => setModalOpen(true)}
          isDirty={!!value}
          isClearable={!!value}
          onClear={(e) => {
            setFieldValue(null, { shouldDirty: true });
            e.stopPropagation();
            e.preventDefault();
          }}
          action={
            <IconButton onClick={() => setModalOpen(true)}>
              <ArrowRightAltIcon />
            </IconButton>
          }
        >
          {value?.qualityCheckName && (
            <Box display="flex" flexDirection="row" flex="0" alignItems="flex-start">
              <Typography
                variant="body2"
                component="span"
                style={{ fontWeight: 600, position: "relative", top: 4 }}
              >
                {value.qualityCheckName}
              </Typography>
              <Box display="flex" flexWrap="wrap" flex="1" gridGap="8px" ml={1}>
                {value?.qualityCheckStatuses.map((status) => (
                  <Chip
                    size="small"
                    key={status}
                    label={
                      <Typography noWrap>
                        {getSearchQualityCheckStatus(status)}
                      </Typography>
                    }
                    onDelete={() => handleRemoveStatus(status)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </NonCollapsibleFavFilter>

        {modal}
      </>
    );
  }

  if (value?.qualityCheckName) {
    return (
      <>
        <ClearBadge onClear={() => setFieldValue(null, { shouldDirty: true })}>
          <div
            style={{
              border: `1px solid ${Color.LightGray}`,
              borderRadius: "4px",
              padding: "8px"
            }}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body1" style={{ color: Color.DetailsInput }}>
                {title}
              </Typography>

              <Box>
                <IconButton
                  style={{
                    marginLeft: 8,
                    padding: 4,
                    width: 32,
                    height: 32,
                    borderRadius: "50%"
                  }}
                >
                  <ArrowRightAltIcon
                    fontSize="small"
                    onClick={() => setModalOpen(true)}
                  />
                </IconButton>
              </Box>
            </Box>

            {value?.qualityCheckName && (
              <Box display="flex" flexDirection="row" alignItems="flex-start" pt={1}>
                <Typography
                  variant="body2"
                  component="span"
                  style={{ fontWeight: 600, position: "relative", top: 4 }}
                >
                  {value.qualityCheckName}
                </Typography>

                <Box display="flex" flexWrap="wrap" flex="1" gridGap="8px">
                  {value?.qualityCheckStatuses.map((status) => (
                    <Chip
                      size="small"
                      key={status}
                      label={
                        <Typography noWrap>
                          {getSearchQualityCheckStatus(status)}
                        </Typography>
                      }
                      onDelete={() => handleRemoveStatus(status)}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </div>
        </ClearBadge>

        {modal}
      </>
    );
  }

  return (
    <>
      <ClearBadge invisible>
        <div className={classes.favouriteButton} onClick={() => setModalOpen(true)}>
          <Typography variant="body1" style={{ color: Color.DetailsInput }}>
            {locale.QualityCheckDocument}
          </Typography>

          <ArrowRightAltIcon fontSize="small" style={{ marginLeft: 8 }} />
        </div>
      </ClearBadge>

      {modal}
    </>
  );
}

const useStyles = makeStyles({
  favouriteButton: {
    border: `1px solid ${Color.LightGray}`,
    borderRadius: "4px",
    padding: "8px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",

    "&:hover": {
      backgroundColor: Color.LightGray
    }
  }
});
